/**
 * Identity hook — loads, generates and updates the user's brand kit (Module 2).
 *
 * Plus two landing-specific mutations:
 *   - regenerateLanding         — Inea produces a fresh multi-section landing.
 *   - regenerateLandingSection  — Inea regenerates one section only.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/api';

export interface BrandPalette {
  name: string;
  colors: string[];
}

/** One bio variant with its strategic angle. */
export interface BioVariant {
  angle: string;
  text: string;
}

// ── Multi-section capture page (landing) ────────────────────────────────────

export type SectionType =
  | 'hero'
  | 'benefits_rich'
  | 'testimonials'
  | 'before_after'
  | 'faq'
  | 'cta_bottom';

export interface RichBenefit {
  icon: string;
  title: string;
  text: string;
}

export interface Testimonial {
  name: string;
  role: string;
  quote: string;
  result: string;
  avatar?: string;
  avatar_url?: string;
  avatar_prompt?: string;
}

export interface FaqItem {
  question: string;
  answer: string;
}

export interface LandingSection {
  type: SectionType;
  enabled: boolean;
}

export type HeroMediaProvider = 'youtube' | 'vimeo' | 'loom' | 'wistia' | 'mp4' | 'none';
export interface HeroMedia {
  provider: HeroMediaProvider;
  url: string;
}

export interface VariantB {
  enabled: boolean;
  headline: string;
  cta: string;
}

export interface CapturePage {
  headline: string;
  promise: string;
  benefits: string[];
  cta: string;
  hero_media?: HeroMedia;
  variant_b?: VariantB;
  benefits_rich: RichBenefit[];
  testimonials: Testimonial[];
  before_after: { before: string; after: string };
  faq: FaqItem[];
  cta_bottom: { headline: string; urgency: string; cta: string };
  sections: LandingSection[];
}

export const DEFAULT_SECTIONS: LandingSection[] = [
  { type: 'hero', enabled: true },
  { type: 'benefits_rich', enabled: true },
  { type: 'testimonials', enabled: true },
  { type: 'before_after', enabled: true },
  { type: 'faq', enabled: true },
  { type: 'cta_bottom', enabled: true },
];

export interface BrandKit {
  /** Language of the stored content (driven by user preferred_lang at gen time). */
  lang?: string;
  /** True when the stored content language doesn t match the active UI language. */
  stale?: boolean;
  /** True when the interview was redone after this kit was generated (12/10/2026). */
  outdated?: boolean;
  bios: { instagram: BioVariant[]; tiktok: BioVariant[] };
  palettes: BrandPalette[];
  typos: { title: string; body: string; visual_style?: string };
  capture_page: CapturePage;
}

export function useIdentity() {
  const qc = useQueryClient();

  const query = useQuery<BrandKit | null>({
    queryKey: ['identity'],
    queryFn: () => apiRequest<BrandKit | null>('/identity'),
  });

  const generate = useMutation({
    mutationFn: () => apiRequest<BrandKit>('/identity/generate', { method: 'POST' }),
    onSuccess: (kit) => qc.setQueryData(['identity'], kit),
  });

  const update = useMutation({
    mutationFn: (patch: Partial<BrandKit>) =>
      apiRequest<BrandKit>('/identity', { method: 'PUT', body: patch }),
    // La réponse du PUT n'a ni lang, ni stale, ni outdated : on les garde.
    onSuccess: (kit) => qc.setQueryData<BrandKit | null>(['identity'], (cur) => ({ ...(cur ?? {}), ...kit })),
  });

  const regenerateLanding = useMutation({
    mutationFn: () =>
      apiRequest<CapturePage>('/identity/landing/regenerate', { method: 'POST' }),
    onSuccess: (cp) => {
      const current = qc.getQueryData<BrandKit | null>(['identity']);
      if (current) qc.setQueryData(['identity'], { ...current, capture_page: cp });
    },
  });

  const regenerateLandingSection = useMutation({
    mutationFn: (type: SectionType) =>
      apiRequest<CapturePage>(`/identity/landing/regenerate/${type}`, { method: 'POST' }),
    onSuccess: (cp) => {
      const current = qc.getQueryData<BrandKit | null>(['identity']);
      if (current) qc.setQueryData(['identity'], { ...current, capture_page: cp });
    },
  });

  return {
    kit: query.data ?? null,
    isLoading: query.isLoading,
    generate,
    update,
    regenerateLanding,
    regenerateLandingSection,
  };
}
